import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '../../i18n';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import {
  EmailResetError,
  emailResetService,
  type EmailResetDetails,
  type EmailResetFailure,
} from '../../services/emailResetService';
import './EmailResetPage.css';

/**
 * Where an emailed email-reset link lands. Somebody managing staff asked for
 * this person's sign-in email to be changed; nothing has changed yet, and
 * nothing does unless they confirm here with the password they have today.
 * Somebody invited who has never signed in has no password: the link alone
 * confirms, and they are taken on to finish setting up.
 */
export default function EmailResetPage() {
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [reset, setReset] = useState<EmailResetDetails | null>(null);
  const [failure, setFailure] = useState<EmailResetFailure | 'unavailable' | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [confirmedEmail, setConfirmedEmail] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setFailure('invalid');
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const details = await emailResetService.fetchReset(token);
        if (!cancelled) setReset(details);
      } catch (err) {
        if (cancelled) return;
        // A wrong_password or password_required answer only ever comes from confirming.
        setFailure(err instanceof EmailResetError ? err.reason as EmailResetFailure : 'unavailable');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const failureCopy = (reason: EmailResetFailure | 'unavailable'): { title: string; body: string } => {
    switch (reason) {
      case 'used':
        return {
          title: t('This link has already been used'),
          body: t('Your email has been changed. Sign in with your new email and the password you already have.'),
        };
      case 'expired':
        return {
          title: t('This link has expired'),
          body: t('Email reset links work for seven days. Your sign-in has not changed. Ask your manager to send a new link.'),
        };
      case 'cancelled':
        return {
          title: t('This link is no longer active'),
          body: t('It was withdrawn or replaced by a newer one. Your sign-in has not changed.'),
        };
      case 'locked':
        return {
          title: t('This link has been locked'),
          body: t('It was tried with the wrong password too many times. Your sign-in has not changed. Ask your manager to send a new link.'),
        };
      case 'revoked':
        return {
          title: t('This account is not active'),
          body: t('Its email cannot be changed. Ask your manager to check your account.'),
        };
      case 'taken':
        return {
          title: t('This email is now in use'),
          body: t('Another account took this address after the link was sent. Your sign-in has not changed. Ask your manager to send a link to a different address.'),
        };
      case 'unavailable':
        return {
          title: t('We could not load this link'),
          body: t('Check your connection and open the link from your email again.'),
        };
      default:
        return {
          title: t('This link is not valid'),
          body: t('Open the link from your email again. If it still does not work, ask your manager to send a new one.'),
        };
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!token || !reset || isSubmitting) return;

    if (reset.requiresPassword && !password) {
      setError(t('Enter your current password to confirm'));
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const result = await emailResetService.confirm(token, reset.requiresPassword ? password : null);
      if (result.inviteToken) {
        // Not set up yet: the address is theirs now, and set-up is what is left.
        navigate(`/staff-invite?token=${encodeURIComponent(result.inviteToken)}`, { replace: true });
        return;
      }
      setConfirmedEmail(result.email || reset.newEmail);
    } catch (err) {
      if (!(err instanceof EmailResetError)) {
        setError((err as Error).message || t('Your email could not be changed. Try again in a moment.'));
      } else if (err.reason === 'wrong_password') {
        setPassword('');
        setError(t('That password is not right. After five wrong tries this link stops working.'));
      } else if (err.reason === 'password_required') {
        // They have signed in since the page loaded, so there is a password to ask for after all.
        setReset({ ...reset, requiresPassword: true });
        setError(t('Enter your current password to confirm'));
      } else {
        setFailure(err.reason);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="auth-layout email-reset__loading">
        <div className="spinner" />
      </div>
    );
  }

  if (failure) {
    const copy = failureCopy(failure);
    return (
      <div className="auth-layout">
        <div className="auth-layout__lang">
          <LanguageSwitcher />
        </div>
        <div className="auth-card email-reset__card">
          <h1 className="auth-card__title">{copy.title}</h1>
          <p className="auth-card__subtitle">{copy.body}</p>
          {failure === 'used' && (
            <button type="button" className="btn btn--primary btn--full-width" onClick={() => navigate('/')}>
              {t('Go to sign in')}
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!reset) return null;

  if (confirmedEmail) {
    return (
      <div className="auth-layout">
        <div className="auth-layout__lang">
          <LanguageSwitcher />
        </div>
        <div className="auth-card email-reset__card" role="status">
          <h1 className="auth-card__title">{t('Your email has been changed')}</h1>
          <p className="auth-card__subtitle">
            {t('From now on, sign in to the Odinseye app and workspace with {email}. Your password has not changed.', { email: confirmedEmail })}
          </p>
          <button type="button" className="btn btn--primary btn--full-width" onClick={() => navigate('/')}>
            {t('Go to sign in')}
          </button>
        </div>
      </div>
    );
  }

  const companyName = reset.companyName ?? 'OdinsEye';

  return (
    <div className="auth-layout">
      <div className="auth-layout__lang">
        <LanguageSwitcher />
      </div>
      <form onSubmit={handleSubmit} className="auth-card email-reset__card" noValidate>
        <h1 className="auth-card__title">{t('Confirm your new email')}</h1>
        <p className="auth-card__subtitle">
          {t('{company} has asked to change the email you sign in to Odinseye with. Nothing changes unless you confirm.', { company: companyName })}
        </p>

        <div aria-live="polite">
          {error && <div className="msg--error" role="alert">{error}</div>}
        </div>

        <div className="form__group">
          <label className="form__label" htmlFor="email-reset-new-email">{t('New email')}</label>
          <input
            id="email-reset-new-email"
            type="email"
            value={reset.newEmail}
            readOnly
            autoComplete="username"
            className="form__input form__input--readonly"
          />
        </div>

        {reset.requiresPassword ? (
          <div className="form__group email-reset__group--last">
            <label className="form__label" htmlFor="email-reset-password">{t('Current password')}</label>
            <input
              id="email-reset-password"
              type="password"
              className="form__input"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              aria-describedby="email-reset-password-hint"
              autoComplete="current-password"
            />
            <p className="email-reset__hint" id="email-reset-password-hint">
              {t('The password you sign in with today. It stays the same.')}
            </p>
          </div>
        ) : (
          <p className="email-reset__note">
            {t('You have not set up your account yet. Confirming takes you on to choose a password.')}
          </p>
        )}

        <button type="submit" disabled={isSubmitting} className="btn btn--primary btn--full-width">
          {isSubmitting ? t('Confirming...') : t('Confirm new email')}
        </button>

        <p className="email-reset__footnote">
          {t('Not expecting this? Close this page and your sign-in stays exactly as it is.')}
        </p>
      </form>
    </div>
  );
}
